import { useScrollReveal } from "@/hooks/useScrollReveal";
import { cn } from "@/lib/utils";
import { SOCIAL_LINKS } from "@/lib/socialLinks";

const SOCIALS = [
  {
    platform: "Instagram",
    url: SOCIAL_LINKS.instagram.href,
    embedUrl: "https://www.instagram.com/plymhair/embed",
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    ),
    color: "from-[#833AB4] via-[#E1306C] to-[#F77737]",
  },
  {
    platform: "Facebook",
    url: SOCIAL_LINKS.facebook.href,
    embedUrl:
      "https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2Fplymhairextensions%2F&tabs=timeline&width=340&height=500&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=false",
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
    color: "from-[#1877F2] to-[#0C5DC7]",
  },
  {
    platform: "TikTok",
    url: SOCIAL_LINKS.tiktok.href,
    embedUrl: "https://www.tiktok.com/embed/@plymhair",
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
      </svg>
    ),
    color: "from-[#000000] to-[#25F4EE]",
  },
];

const SocialFeed = () => {
  const { ref, visible } = useScrollReveal();

  return (
    <section
      ref={ref}
      className={cn(
        "py-16 md:py-24 bg-secondary transition-all duration-700 ease-out",
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      )}
    >
      <div className="container mx-auto px-4">
        <div className={cn("text-center mb-12 transition-all duration-700 delay-100 ease-out", visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6")}>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-3">
            Follow Us
          </h2>
          <p className="text-muted-foreground font-body max-w-lg mx-auto">
            Stay connected — see our latest styles, tutorials and behind-the-scenes content.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {SOCIALS.map((s, i) => (
            <div
              key={s.platform}
              className={cn(
                "rounded-xl border border-border bg-card overflow-hidden shadow-sm hover:shadow-md transition-all duration-500 ease-out",
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
              style={{ transitionDelay: visible ? `${200 + i * 100}ms` : "0ms" }}
            >
              <div className={`bg-gradient-to-r ${s.color} px-4 py-3 flex items-center gap-2 text-white`}>
                {s.icon}
                <span className="font-display font-semibold text-sm tracking-wide">
                  {s.platform}
                </span>
              </div>

              <div className="w-full h-[420px] overflow-hidden bg-muted">
                {visible ? (
                  <iframe
                    src={s.embedUrl}
                    className="w-full h-full border-0"
                    allow="encrypted-media"
                    loading="lazy"
                    title={`${s.platform} feed`}
                  />
                ) : null}
              </div>

              <a
                href={s.url}
                target={s.url.startsWith("http") ? "_blank" : undefined}
                rel={s.url.startsWith("http") ? "noopener" : undefined}
                className="block text-center py-3 font-body text-sm font-semibold text-primary hover:underline"
              >
                Follow @plymhair →
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SocialFeed;
